import { Fragment, useEffect, useMemo, useState, type MutableRefObject } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useAuth } from '@/auth/useAuth';
import { useActingOrg } from '@/auth/actingOrgContext';
import { useCan } from '@/auth/consoleCapabilityContext';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import {
    DropdownMenu,
    EmptyState,
    ErrorState,
    Modal,
    SkeletonTable,
    useToast,
    type DropdownMenuItem,
} from '@/components/ui';
import {
    createUserGroup,
    fetchUserGroups,
    updateUserGroup,
    type ConsoleUserGroup,
} from '@/api/orgAdmin';
import { CaretIcon, FilterIcon, SearchIcon } from './icons';

export interface UserGroupsTabProps {
    /** The page header owns the "+ Tạo nhóm" button; this tab owns the modal. */
    createRef: MutableRefObject<(() => void) | null>;
}

type FilterKey = 'all' | 'active' | 'inactive' | 'unassigned';

/**
 * EAA-1843: "Nhóm người dùng" — frame 04.
 *
 * Scoped to the acting organization, mirroring the model: a group belongs to
 * exactly one org, so a global list would misrepresent it. The org shown in the
 * page header is that same acting-org context rendered compactly, not a second
 * selector — two independent org controls on one screen is how you end up
 * editing the wrong org's groups.
 *
 * Rows group under CÓ VAI TRÒ / CHƯA GÁN VAI TRÒ, which is the split an operator
 * actually scans for: a group holding no role grants nothing, so "did I forget to
 * wire this one up" is a structural question, not a column value to compare.
 *
 * Chip counts come from the UNFILTERED list, the same rule the catalog tab
 * follows — a chip that recounted itself against the active search would read
 * "Đã ngưng 0" while you type, which says "there are none" rather than "none
 * match".
 */
export function UserGroupsTab({ createRef }: UserGroupsTabProps) {
    const { t } = useTranslation();
    const can = useCan();
    const toast = useToast();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const acting = useActingOrg();
    const queryClient = useQueryClient();
    const orgId = acting?.actingOrgId ?? null;
    const canManage = can('console.groups.manage');

    // EAA-1845: built inside the component so the chip labels go through t() —
    // this used to be a module-level FILTER_CHIPS constant.
    const FILTER_CHIPS: [FilterKey, string][] = [
        ['all', t('console.permissions.groups.filterAll')],
        ['active', t('console.permissions.groups.filterActive')],
        ['inactive', t('console.permissions.groups.filterInactive')],
        ['unassigned', t('console.permissions.groups.filterUnassigned')],
    ];

    const [editing, setEditing] = useState<ConsoleUserGroup | null>(null);
    const [creating, setCreating] = useState(false);
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState<FilterKey>('all');
    const [sortAsc, setSortAsc] = useState(true);

    const groupsQuery = useQuery({
        queryKey: ['user-groups', accessToken, orgId],
        queryFn: () => fetchUserGroups(config.apiBase, accessToken as string, orgId as string),
        enabled: Boolean(orgId && accessToken),
        retry: false,
    });

    const all = useMemo(() => groupsQuery.data ?? [], [groupsQuery.data]);

    const counts = useMemo(
        () => ({
            all: all.length,
            active: all.filter((g) => g.is_active).length,
            inactive: all.filter((g) => !g.is_active).length,
            unassigned: all.filter((g) => g.roles.length === 0).length,
        }),
        [all]
    );

    const visible = useMemo(() => {
        const term = search.trim().toLowerCase();
        return all.filter((g) => {
            if (filter === 'active' && !g.is_active) return false;
            if (filter === 'inactive' && g.is_active) return false;
            if (filter === 'unassigned' && g.roles.length > 0) return false;
            return !term || g.name.toLowerCase().includes(term);
        });
    }, [all, search, filter]);

    /** CÓ VAI TRÒ first, then CHƯA GÁN VAI TRÒ — the split that matters. */
    const sections = useMemo(() => {
        const sorted = [...visible].sort((a, b) =>
            sortAsc ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name)
        );
        const withRole = sorted.filter((g) => g.roles.length > 0);
        const without = sorted.filter((g) => g.roles.length === 0);
        return [
            { key: 'has', label: t('console.permissions.groups.sectionHasRole'), rows: withRole },
            { key: 'none', label: t('console.permissions.groups.filterUnassigned'), rows: without },
        ].filter((s) => s.rows.length > 0);
    }, [visible, sortAsc, t]);

    /**
     * The kebab's only real action. A group cannot be deleted — role assignments
     * point at it, and dropping the row would silently revoke whatever it granted
     * — so `is_active` is the off switch, and an inactive group keeps its history
     * readable instead of vanishing.
     */
    const setActive = useMutation({
        mutationFn: ({ group, active }: { group: ConsoleUserGroup; active: boolean }) =>
            updateUserGroup(config.apiBase, accessToken as string, orgId as string, group.id, {
                is_active: active,
            }),
        onSuccess: (_data, { active }) => {
            toast.success(
                active
                    ? t('console.permissions.groups.reactivatedToast')
                    : t('console.permissions.groups.deactivatedToast')
            );
            void queryClient.invalidateQueries({
                queryKey: ['user-groups', accessToken, orgId],
            });
        },
        onError: (e: unknown) =>
            toast.error(
                e instanceof Error ? e.message : t('console.permissions.groups.toggleErrorToast')
            ),
    });

    function rowActions(group: ConsoleUserGroup): DropdownMenuItem[] {
        return [
            {
                key: 'members',
                label: t('console.permissions.groups.rowActionEditMembers'),
                onSelect: () => setEditing(group),
            },
            group.is_active
                ? {
                      key: 'deactivate',
                      label: t('console.permissions.groups.rowActionDeactivate'),
                      danger: true,
                      disabled: setActive.isPending,
                      onSelect: () => setActive.mutate({ group, active: false }),
                  }
                : {
                      key: 'activate',
                      label: t('console.permissions.groups.rowActionActivate'),
                      disabled: setActive.isPending,
                      onSelect: () => setActive.mutate({ group, active: true }),
                  },
        ];
    }

    // Publish the create trigger for the page header's button. Re-registered every
    // render so it never closes over a stale setter, and cleared on unmount so a
    // header button left behind by a tab switch cannot open this tab's modal.
    useEffect(() => {
        createRef.current = () => setCreating(true);
        return () => {
            createRef.current = null;
        };
    });

    if (!orgId) {
        return <EmptyState title={t('console.permissions.groups.noOrgSelectedTitle')} />;
    }
    if (groupsQuery.isLoading) return <SkeletonTable rows={5} columns={5} />;
    if (groupsQuery.isError) {
        return (
            <ErrorState
                description={t('console.permissions.groups.loadErrorDescription')}
                onRetry={() => void groupsQuery.refetch()}
                retrying={groupsQuery.isFetching}
            />
        );
    }

    return (
        <div className="perm-tab-body">
            <div className="perm-toolbar">
                <label className="perm-search">
                    <SearchIcon />
                    <input
                        type="text"
                        placeholder={t('console.permissions.groups.searchPlaceholder')}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        aria-label={t('console.permissions.groups.searchAriaLabel')}
                    />
                </label>
                <span className="perm-toolbar-right">
                    <button
                        type="button"
                        className="portal-btn perm-filter-btn"
                        onClick={() => setFilter('all')}
                        disabled={filter === 'all'}
                    >
                        <FilterIcon />
                        {t('console.permissions.groups.filterButton', {
                            count: filter === 'all' ? 0 : 1,
                        })}
                    </button>
                    <span className="perm-count" aria-live="polite">
                        {t('console.permissions.groups.countDisplay', {
                            visible: visible.length,
                            all: all.length,
                        })}
                    </span>
                </span>
            </div>

            <div className="perm-chips" role="group" aria-label={t('console.permissions.groups.filterGroupAriaLabel')}>
                {FILTER_CHIPS.map(([key, label]) => (
                    <button
                        key={key}
                        type="button"
                        className={filter === key ? 'perm-chip perm-chip--on' : 'perm-chip'}
                        aria-pressed={filter === key}
                        onClick={() => setFilter(key)}
                    >
                        {label} <span className="perm-chip-n">{counts[key]}</span>
                    </button>
                ))}
            </div>

            <div className="portal-table-scroll perm-table-scroll">
                <table className="portal-admin-table perm-matrix">
                    <thead>
                        <tr>
                            <th scope="col">
                                <button
                                    type="button"
                                    className="perm-sort"
                                    onClick={() => setSortAsc((v) => !v)}
                                    aria-label={t('console.permissions.groups.sortByNameAriaLabel')}
                                >
                                    {t('console.permissions.groups.columnGroup')}
                                    <CaretIcon up={!sortAsc} />
                                </button>
                            </th>
                            <th scope="col" className="perm-num">
                                {t('console.permissions.groups.columnMemberCount')}
                            </th>
                            <th scope="col">{t('console.permissions.groups.columnRolesHeld')}</th>
                            <th scope="col">{t('console.permissions.groups.columnStatus')}</th>
                            <th scope="col">{t('console.permissions.groups.columnUpdated')}</th>
                            {canManage && (
                                <th
                                    scope="col"
                                    aria-label={t('console.permissions.groups.actionsColumnAriaLabel')}
                                />
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {sections.map((section) => (
                            <Fragment key={section.key}>
                                <tr className="perm-section">
                                    <th scope="rowgroup" colSpan={canManage ? 6 : 5}>
                                        {section.label}
                                        <span className="perm-section-n">
                                            {t('console.permissions.groups.sectionCount', {
                                                count: section.rows.length,
                                            })}
                                        </span>
                                    </th>
                                </tr>
                                {section.rows.map((g) => (
                                    <tr
                                        key={g.id}
                                        className={g.is_active ? '' : 'portal-console-row-muted'}
                                    >
                                        <td>
                                            <span className="grp-name">{g.name}</span>
                                        </td>
                                        <td className="perm-num">{g.member_count}</td>
                                        <td>
                                            {g.roles.length === 0 ? (
                                                <span className="grp-norole">
                                                    {t('console.permissions.groups.noRoleBadge')}
                                                </span>
                                            ) : (
                                                <span className="grp-roles">
                                                    {g.roles.map((r) => (
                                                        <span key={r.id} className="grp-role-chip">
                                                            {r.name}
                                                        </span>
                                                    ))}
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            <span
                                                className={
                                                    g.is_active
                                                        ? 'grp-status'
                                                        : 'grp-status grp-status--off'
                                                }
                                            >
                                                <span className="grp-dot" aria-hidden="true" />
                                                {g.is_active
                                                    ? t('console.permissions.groups.filterActive')
                                                    : t('console.permissions.groups.filterInactive')}
                                            </span>
                                        </td>
                                        <td className="grp-updated">
                                            {relativeTime(g.updated_at, t)}
                                        </td>
                                        {canManage && (
                                            <td className="grp-actions-cell">
                                                <span className="grp-actions">
                                                    <button
                                                        type="button"
                                                        className="portal-btn grp-edit"
                                                        onClick={() => setEditing(g)}
                                                    >
                                                        {t('console.permissions.groups.editMembersButton')}
                                                    </button>
                                                    <DropdownMenu
                                                        items={rowActions(g)}
                                                        triggerAriaLabel={t(
                                                            'console.permissions.groups.rowActionsAriaLabel',
                                                            { name: g.name }
                                                        )}
                                                    />
                                                </span>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </Fragment>
                        ))}
                        {visible.length === 0 && (
                            <tr>
                                <td colSpan={canManage ? 6 : 5} className="perm-empty">
                                    {all.length === 0
                                        ? t('console.permissions.groups.emptyNoGroups')
                                        : t('console.permissions.groups.emptyNoMatch')}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <GroupModal
                open={creating || Boolean(editing)}
                group={editing}
                orgId={orgId}
                onClose={() => {
                    setCreating(false);
                    setEditing(null);
                }}
                onSaved={() => {
                    setCreating(false);
                    setEditing(null);
                    toast.success(t('console.permissions.groups.savedGroupToast'));
                    void queryClient.invalidateQueries({
                        queryKey: ['user-groups', accessToken, orgId],
                    });
                }}
            />
        </div>
    );
}

/**
 * "2 ngày trước". Rendered client-side from the server's ISO timestamp rather
 * than shipping a pre-formatted string, so the phrasing stays correct as the page
 * sits open and does not need the server to know the reader's locale.
 *
 * EAA-1845: takes `t` as a parameter rather than calling `useTranslation()`
 * itself — this is a plain function, not a component/hook, so it cannot use
 * the hook directly; the caller (a component) supplies its own `t`.
 */
function relativeTime(iso: string, t: TFunction): string {
    if (!iso) return '—';
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return '—';
    const mins = Math.round((Date.now() - then) / 60000);
    if (mins < 1) return t('console.permissions.groups.relativeJustNow');
    if (mins < 60) return t('console.permissions.groups.relativeMinutesAgo', { count: mins });
    const hours = Math.round(mins / 60);
    if (hours < 24) return t('console.permissions.groups.relativeHoursAgo', { count: hours });
    const days = Math.round(hours / 24);
    if (days < 7) return t('console.permissions.groups.relativeDaysAgo', { count: days });
    const weeks = Math.round(days / 7);
    if (weeks < 5) return t('console.permissions.groups.relativeWeeksAgo', { count: weeks });
    return t('console.permissions.groups.relativeMonthsAgo', {
        count: Math.round(days / 30),
    });
}

function GroupModal({
    open,
    group,
    orgId,
    onClose,
    onSaved,
}: {
    open: boolean;
    group: ConsoleUserGroup | null;
    orgId: string;
    onClose: () => void;
    onSaved: () => void;
}) {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [members, setMembers] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [seeded, setSeeded] = useState<string | null>(null);

    // Seed once per opened group rather than in an effect keyed on every field —
    // re-seeding mid-edit would silently discard what was typed.
    const key = group?.id ?? (open ? '__new__' : null);
    if (key !== seeded) {
        setSeeded(key);
        setName(group?.name ?? '');
        setDescription(group?.description ?? '');
        setMembers((group?.members ?? []).map((m) => m.user_sub).join('\n'));
        setError(null);
    }

    const save = useMutation({
        mutationFn: () => {
            const roster = members
                .split('\n')
                .map((l) => l.trim())
                .filter(Boolean)
                .map((user_sub) => ({ user_sub }));
            const body = { name: name.trim(), description: description.trim(), members: roster };
            return group
                ? updateUserGroup(config.apiBase, accessToken as string, orgId, group.id, body)
                : createUserGroup(config.apiBase, accessToken as string, orgId, body);
        },
        onSuccess: onSaved,
        onError: (e: unknown) =>
            setError(e instanceof Error ? e.message : t('console.permissions.groups.saveErrorToast')),
    });

    const modalTitle = group
        ? t('console.permissions.groups.modalEditTitle', { name: group.name })
        : t('console.permissions.groups.modalCreateTitle');

    return (
        <Modal
            open={open}
            onCancel={onClose}
            title={modalTitle}
            footer={
                <div className="hz-move-footer">
                    <span className="hz-move-footer-note">
                        {t('console.permissions.groups.footerReplaceNote')}
                    </span>
                    <span className="hz-move-footer-actions">
                        <button type="button" className="portal-btn" onClick={onClose}>
                            {t('common.cancel')}
                        </button>
                        <button
                            type="button"
                            className="portal-btn portal-btn-primary"
                            disabled={!name.trim() || save.isPending}
                            onClick={() => {
                                setError(null);
                                save.mutate();
                            }}
                        >
                            {save.isPending
                                ? t('console.permissions.groups.savingPending')
                                : t('console.permissions.groups.saveButton')}
                        </button>
                    </span>
                </div>
            }
        >
            <div className="portal-admin-form">
                <label>
                    <span className="portal-field-label">
                        {t('console.permissions.groups.fieldNameLabel')}
                    </span>
                    <input
                        type="text"
                        value={name}
                        placeholder={t('console.permissions.groups.fieldNamePlaceholder')}
                        onChange={(e) => setName(e.target.value)}
                    />
                </label>
                <label>
                    <span className="portal-field-label">
                        {t('console.permissions.groups.fieldDescriptionLabel')}
                    </span>
                    <input
                        type="text"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                    />
                </label>
                <label>
                    <span className="portal-field-label">
                        {t('console.permissions.groups.fieldMembersLabel')}
                    </span>
                    <textarea
                        rows={6}
                        className="assign-input"
                        value={members}
                        placeholder={t('console.permissions.groups.fieldMembersPlaceholder')}
                        onChange={(e) => setMembers(e.target.value)}
                    />
                </label>
                <p className="portal-admin-hint">
                    {t('console.permissions.groups.membersHint')} <code>sub</code>.
                </p>
                {error && <p className="portal-admin-error">{error}</p>}
            </div>
        </Modal>
    );
}

export default UserGroupsTab;
